import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import type { SoftwareAssuranceWorkflowController } from '@/hooks/workflow/useSoftwareAssuranceWorkflow'

interface SourcesStepSectionProps {
  workflow: SoftwareAssuranceWorkflowController
}

export function SourcesStepSection({ workflow }: SourcesStepSectionProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold">Source Ingestion</h4>
          <p className="text-sm text-muted-foreground">
            Register repository or file system sources for mission scans.
          </p>
        </div>
        <Badge variant="outline">{workflow.sources.length} sources</Badge>
      </div>
      <div className="grid gap-3 rounded-lg border border-border p-4 bg-muted/30">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Source Type</p>
            <Select
              value={workflow.sourceForm.source_type}
              onValueChange={(value) =>
                workflow.setSourceForm((prev) => ({ ...prev, source_type: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select source type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="repo">Repository</SelectItem>
                <SelectItem value="filesystem">Filesystem</SelectItem>
                <SelectItem value="url">URL</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Label (optional)</p>
            <Input
              placeholder="Mission repo"
              value={workflow.sourceForm.label}
              onChange={(event) =>
                workflow.setSourceForm((prev) => ({ ...prev, label: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Location</p>
          <Input
            placeholder="Repository URL or filesystem path"
            value={workflow.sourceForm.location}
            onChange={(event) =>
              workflow.setSourceForm((prev) => ({ ...prev, location: event.target.value }))
            }
          />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Branch</p>
            <Input
              placeholder="main"
              value={workflow.sourceForm.branch}
              onChange={(event) =>
                workflow.setSourceForm((prev) => ({ ...prev, branch: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Commit</p>
            <Input
              placeholder="Optional commit hash"
              value={workflow.sourceForm.commit}
              onChange={(event) =>
                workflow.setSourceForm((prev) => ({ ...prev, commit: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Auth Type</p>
            <Input
              placeholder="bearer, ssh, none"
              value={workflow.sourceForm.auth_type}
              onChange={(event) =>
                workflow.setSourceForm((prev) => ({ ...prev, auth_type: event.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Access Token (optional)</p>
            <Input
              type="password"
              placeholder="Stored securely server-side"
              value={workflow.sourceForm.accessToken}
              onChange={(event) =>
                workflow.setSourceForm((prev) => ({ ...prev, accessToken: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={workflow.handleAddSource}>Register Source</Button>
          {workflow.sourceStatus && <span className="text-xs text-muted-foreground">{workflow.sourceStatus}</span>}
        </div>
      </div>
      {workflow.sources.length > 0 && (
        <div className="grid gap-2">
          {workflow.sources.map((source) => (
            <div key={source.source_id} className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{source.label || source.location}</p>
                  <p className="text-xs text-muted-foreground">
                    {source.source_type} {source.branch ? `• ${source.branch}` : ''}{' '}
                    {source.commit ? `• ${source.commit.slice(0, 7)}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{source.status}</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => workflow.setTargetPath(source.location)}
                  >
                    Use for Scans
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
